import React, { useState, useEffect } from 'react';
import { Shield, Eye, Smartphone, Globe, MapPin, Server, AlertTriangle, Lock, CheckCircle, X, Wifi, Layers, Camera, AlertCircle, HelpCircle, Info, Home, Brain, Fingerprint, CreditCard, Download, RotateCcw } from 'lucide-react';

const PrivacyAssessment = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem('privacyAssessment_answers');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [showResults, setShowResults] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('main');
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return !localStorage.getItem('privacyAssessment_welcomed') &&
             !localStorage.getItem('privacyAssessment_answers');
    } catch { return false; }
  });

  // Central gate (ThreatWatch / microtools): gated UP FRONT — the first answer triggers requireRun
  // (member-free / credits / Tools plan / one-time guest pay). gate.js shows the unlock overlay.
  const [started, setStarted] = useState(false);
  const ensureStarted = async () => {
    if (started) return true;
    const gate = window.csmGate;
    const ok = gate?.requireRun ? await gate.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };

  const dismissWelcome = () => {
    try { localStorage.setItem('privacyAssessment_welcomed', 'true'); } catch {}
    setShowWelcome(false);
  };

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem('privacyAssessment_answers', JSON.stringify(answers));
    }
  }, [answers]);

  // Resume after a guest-pay / sign-in bounce (answers already persist to localStorage above).
  useEffect(() => {
    const gate = window.csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && gate?.requireRun) {
      gate.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []);

  const hasSavedProgress = Object.keys(answers).length > 0;

  const sections = [
    {
      id: 'device',
      title: 'Device Security',
      icon: <Smartphone className="h-6 w-6" />,
      questions: [
        {
          id: 'device_1',
          question: 'Do you use full-disk encryption on all your devices?',
          options: ['Yes', 'Only on some devices', 'No'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'device_2',
          question: 'How often do you update your operating systems and applications?',
          options: ['Immediately when available', 'Within a few days', 'Occasionally', 'Rarely/Never'],
          riskLevels: [0, 1, 2, 3]
        },
        {
          id: 'device_3',
          question: 'Do you use strong, unique PINs or passwords to lock your devices?',
          options: ['Yes, for all devices', 'For some devices', 'No'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'device_4',
          question: 'Have you disabled unnecessary permissions for apps on your mobile devices?',
          options: ['Yes, regularly review permissions', 'Sometimes check permissions', 'Never check app permissions'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'device_5',
          question: 'Do you use a hardened operating system (GrapheneOS, CalyxOS, Qubes OS, Tails, etc.)?',
          options: ['Yes, for sensitive operations', 'No, but I use privacy-focused OS settings', 'No, I use default OS configurations'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'device_6',
          question: 'Have you disabled or covered unnecessary hardware components (microphones, cameras) when not in use?',
          options: ['Yes, physically and/or through software', 'Sometimes', 'No'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'comms',
      title: 'Communications Security',
      icon: <Globe className="h-6 w-6" />,
      questions: [
        {
          id: 'comms_1',
          question: 'Do you use end-to-end encrypted messaging for sensitive communications?',
          options: ['Yes, always (Signal, Element, etc.)', 'Sometimes', 'No'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'comms_2',
          question: 'Do you use a VPN when connecting to public WiFi networks?',
          options: ['Yes, always', 'Sometimes', 'Never'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'comms_3',
          question: 'Do you use DNS-over-HTTPS or a privacy-focused DNS provider?',
          options: ['Yes (Quad9, NextDNS, Cloudflare 1.1.1.1)', 'Not sure', 'No, using default ISP DNS'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'comms_4',
          question: 'Do you use ephemeral messaging (messages that automatically delete)?',
          options: ['Yes, for sensitive communications', 'Sometimes', 'No'],
          riskLevels: [0, 1, 2]
        },
        {
          id: 'comms_5',
          question: 'Do you verify security keys/safety numbers when establishing secure communications?',
          options: ['Yes, always for important contacts', 'Sometimes/for some contacts', 'No/Not sure what this means'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'comms_6',
          question: 'Do you use different identities/personas for different communication contexts?',
          options: ['Yes, I compartmentalize systematically', 'Sometimes for sensitive communications', 'No, I use the same identity everywhere'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'data',
      title: 'Data Protection',
      icon: <Server className="h-6 w-6" />,
      questions: [
        {
          id: 'data_1',
          question: 'Do you encrypt sensitive data before storing it in the cloud?',
          options: ['Yes, always (Cryptomator, Boxcryptor, etc.)', 'Sometimes', 'No', 'I don\'t use cloud storage'],
          riskLevels: [0, 2, 3, 1]
        },
        {
          id: 'data_2',
          question: 'Do you use a password manager?',
          options: ['Yes (Bitwarden, 1Password, etc.)', 'No, I memorize strong unique passwords', 'No, I reuse passwords or use simple ones'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'data_3',
          question: 'Do you regularly backup your data?',
          options: ['Yes, automatically and encrypted', 'Yes, but not encrypted', 'Occasionally', 'No'],
          riskLevels: [0, 1, 2, 3]
        },
        {
          id: 'data_4',
          question: 'Have you reviewed privacy settings on your social media accounts?',
          options: ['Yes, regularly', 'Yes, but not recently', 'Never', 'I don\'t use social media'],
          riskLevels: [0, 1, 3, 0]
        }
      ]
    },
    {
      id: 'location',
      title: 'Location Privacy',
      icon: <MapPin className="h-6 w-6" />,
      questions: [
        {
          id: 'location_1',
          question: 'Do you leave location services enabled on your mobile devices?',
          options: ['Only when needed for specific apps', 'Usually enabled', 'Always enabled'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'location_2',
          question: 'Do you check in or share your location on social media?',
          options: ['Never', 'Occasionally but after leaving the location', 'Regularly'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'location_3',
          question: 'Do you use maps/navigation apps that don\'t track your location history?',
          options: ['Yes (Organic Maps, OsmAnd, etc.)', 'Sometimes', 'No (Google Maps, Apple Maps with history enabled)', 'Not sure'],
          riskLevels: [0, 1, 3, 2]
        },
        {
          id: 'location_4',
          question: 'Do you regularly review which apps have access to your location?',
          options: ['Yes, frequently', 'Occasionally', 'Never'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'identity',
      title: 'Identity Protection',
      icon: <Eye className="h-6 w-6" />,
      questions: [
        {
          id: 'identity_1',
          question: 'What type of multi-factor authentication (MFA) do you primarily use for important accounts?',
          options: ['Hardware security keys or passkeys (YubiKey, device passkeys)', 'Authenticator app (TOTP — Ente Auth, Aegis, etc.)', 'SMS-based codes or no MFA'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'identity_2',
          question: 'How much personal information is publicly available about you online?',
          options: ['Very little — I\'ve actively minimized it', 'Some basic information', 'Significant amount'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'identity_3',
          question: 'Do you use email aliases or separate addresses for different purposes?',
          options: ['Yes, use aliases/multiple addresses (SimpleLogin, AnonAddy, etc.)', 'A few different addresses', 'Single email for everything'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'identity_4',
          question: 'Have you configured privacy settings in your browser (cookies, tracking, etc.)?',
          options: ['Yes, use privacy-focused browser/extensions', 'Basic settings only', 'Default settings'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'identity_5',
          question: 'Have you removed your information from data brokers and people search sites?',
          options: ['Yes, comprehensively (Incogni, DeleteMe, or manually)', 'Partially removed from some services', 'No/Not aware of this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'identity_6',
          question: 'Do you use unique usernames/identifiers across different online services?',
          options: ['Yes, unique for most/all services', 'Sometimes use different usernames', 'Use same username everywhere'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'browser',
      title: 'Browser Security',
      icon: <Globe className="h-6 w-6" />,
      questions: [
        {
          id: 'browser_1',
          question: 'Do you use a privacy-focused browser or privacy extensions?',
          options: ['Yes, both (Firefox/Brave + uBlock Origin, etc.)', 'Privacy extensions only', 'Standard browser with default settings'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'browser_2',
          question: 'Have you disabled WebRTC and other potential IP leak vectors in your browser?',
          options: ['Yes', 'Not sure', 'No'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'browser_3',
          question: 'How frequently do you clear browsing history, cookies, and cache?',
          options: ['After each sensitive session or daily', 'Weekly', 'Rarely/Never'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'browser_4',
          question: 'Do you use browser compartmentalization (different browsers or profiles for different activities)?',
          options: ['Yes, systematically separated', 'Sometimes for sensitive activities', 'No'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'browser_5',
          question: 'Have you reviewed or disabled AI features in your browser (Edge Copilot, Chrome AI, Gemini in Chrome) that send data to the cloud?',
          options: ['Yes, reviewed and disabled unwanted AI browser features', 'Partially reviewed', 'Using default browser AI settings without review'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'browser_6',
          question: 'Do you take measures to prevent browser fingerprinting?',
          options: ['Yes, use anti-fingerprinting tools (Mullvad Browser, Firefox resist fingerprinting)', 'Basic measures only', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'network',
      title: 'Network Security',
      icon: <Wifi className="h-6 w-6" />,
      questions: [
        {
          id: 'network_1',
          question: 'Have you changed default credentials on your home router/network devices?',
          options: ['Yes, all devices have strong custom credentials', 'Some devices only', 'No, using defaults'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'network_2',
          question: 'Do you segregate IoT devices onto a separate network?',
          options: ['Yes, separate VLAN/network', 'Some isolation measures (guest network)', 'No, everything on same network'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'network_3',
          question: 'Do you use a custom DNS provider that doesn\'t log queries?',
          options: ['Yes, privacy-focused DNS (Quad9, NextDNS, etc.)', 'Not sure', 'No, default ISP DNS'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'network_4',
          question: 'Have you implemented network-level ad/tracking blocking?',
          options: ['Yes, Pi-hole, AdGuard Home, or NextDNS', 'Basic DNS filtering', 'No'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'network_5',
          question: 'Do you monitor for unusual network traffic or connections?',
          options: ['Yes, active monitoring', 'Basic/occasional checks', 'No monitoring'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'network_6',
          question: 'Have you disabled unused network services on your devices (UPnP, WPS, etc.)?',
          options: ['Yes, systematically', 'Some services disabled', 'No/Not sure how'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'opsec',
      title: 'Advanced Operational Security',
      icon: <Layers className="h-6 w-6" />,
      questions: [
        {
          id: 'opsec_1',
          question: 'Have you conducted threat modeling based on your specific adversary capabilities?',
          options: ['Yes, comprehensive modeling', 'Basic threat assessment', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'opsec_2',
          question: 'Do you use secure data deletion beyond standard deletion?',
          options: ['Yes, use secure wiping tools (BleachBit, Eraser, shred)', 'Sometimes for sensitive data', 'No, regular deletion only'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'opsec_3',
          question: 'Do you use air-gapped computing for highly sensitive operations?',
          options: ['Yes, when required', 'Rarely/only for extremely sensitive data', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'opsec_4',
          question: 'Do you strip metadata from files and images before sharing?',
          options: ['Yes, systematically (MAT2, ExifTool, etc.)', 'For some files/communications', 'No/Not aware of this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'opsec_5',
          question: 'Do you verify software/files using cryptographic signatures before installing?',
          options: ['Yes, always for important software', 'Sometimes', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'opsec_6',
          question: 'Do you have data compromise response protocols in place?',
          options: ['Yes, documented procedures', 'Basic plan only', 'No plan'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'physical',
      title: 'Physical Security & Surveillance',
      icon: <Camera className="h-6 w-6" />,
      questions: [
        {
          id: 'physical_1',
          question: 'Are you aware of camera network avoidance techniques in public spaces?',
          options: ['Yes, implement regularly', 'Basic awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'physical_2',
          question: 'Do you implement audio surveillance countermeasures when needed (white noise, Faraday bags)?',
          options: ['Yes, when discussing sensitive topics', 'Rarely', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'physical_3',
          question: 'Do you practice visual privacy (privacy screen filters, desk positioning, locking screen when away)?',
          options: ['Yes, comprehensive measures', 'Basic measures only', 'No measures'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'physical_4',
          question: 'Are you familiar with Technical Surveillance Countermeasures (TSCM)?',
          options: ['Yes, implement when needed', 'Basic knowledge only', 'No knowledge'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'physical_5',
          question: 'Do you maintain awareness of your physical environment when handling sensitive information?',
          options: ['Yes, highly vigilant', 'Sometimes aware', 'Rarely consider this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'physical_6',
          question: 'Do you secure physical documents and devices appropriately?',
          options: ['Yes, systematic security measures', 'Basic measures only', 'No special measures'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'govt',
      title: 'Government-Level Surveillance',
      icon: <AlertCircle className="h-6 w-6" />,
      questions: [
        {
          id: 'govt_1',
          question: 'Are you aware of current surveillance laws affecting your data (FISA reauthorization, EU AI Act data provisions, state privacy laws)?',
          options: ['Yes, actively monitor legal developments', 'General awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'govt_2',
          question: 'Are you familiar with legal instruments that compel data disclosure (National Security Letters, gag orders, MLAT)?',
          options: ['Yes, detailed understanding', 'Basic awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'govt_3',
          question: 'Do you consider data sovereignty and jurisdiction when choosing service providers?',
          options: ['Yes, always factor this in', 'Sometimes consider this', 'Never consider this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'govt_4',
          question: 'Do you use Tor or similar anonymity networks for sensitive browsing?',
          options: ['Yes, for appropriate activities', 'Basic additional measures', 'Standard encryption only'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'govt_5',
          question: 'Are you aware of which of your communications might be subject to legal intercept obligations?',
          options: ['Yes, comprehensive awareness', 'Some awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'govt_6',
          question: 'Do you implement countermeasures against traffic analysis?',
          options: ['Yes, systematically (Tor, VPN chaining, timing obfuscation)', 'For some communications', 'No/Not familiar with this'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'smarthome',
      title: 'Smart Home Security',
      icon: <Home className="h-6 w-6" />,
      questions: [
        {
          id: 'smarthome_1',
          question: 'Do you regularly update firmware on smart home/IoT devices?',
          options: ['Yes, all devices are updated promptly', 'Some devices are updated', 'Rarely/never update'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'smarthome_2',
          question: 'Have you changed default passwords on all smart home devices?',
          options: ['Yes, all devices have unique strong passwords', 'Some devices have changed passwords', 'Using default credentials'],
          riskLevels: [0, 2, 3]
        },
        {
          id: 'smarthome_3',
          question: 'Do you segregate smart home devices on a separate network from your main devices?',
          options: ['Yes, separate VLAN/network', 'Some isolation measures', 'All devices on same network'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'smarthome_4',
          question: 'Have you reviewed and minimized data collection settings on smart home devices?',
          options: ['Yes, minimized on all devices', 'On some devices', 'No/Not aware this is possible'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'smarthome_5',
          question: 'Do you disable microphones/cameras on smart devices when not in use?',
          options: ['Yes, systematically', 'Sometimes', 'No/Not possible'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'smarthome_6',
          question: 'Do you research privacy practices of manufacturers before purchasing smart devices?',
          options: ['Yes, thoroughly', 'Sometimes/briefly', 'No consideration'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'ai',
      title: 'AI & ML Exposure',
      icon: <Brain className="h-6 w-6" />,
      questions: [
        {
          id: 'ai_1',
          question: 'Do you take measures to limit your exposure to facial recognition systems in public?',
          options: ['Yes, systematic precautions', 'Basic awareness and occasional measures', 'No precautions'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'ai_2',
          question: 'Have you reviewed and limited what AI assistants/agents (ChatGPT, Copilot, Gemini, Claude) can access on your accounts or devices?',
          options: ['Yes, denied or strictly limited AI agent access to email/files/calendar', 'Reviewed but granted broad access', 'No, using default or unrestricted settings'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'ai_3',
          question: 'Are you aware of how your data might be used for algorithmic profiling by AI systems?',
          options: ['Yes, comprehensive understanding', 'Basic awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'ai_4',
          question: 'Do you understand the risks of synthetic media (deepfakes, AI voice cloning) and how to identify them?',
          options: ['Yes, well-informed and use verification methods', 'Basic knowledge', 'No knowledge'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'ai_5',
          question: 'Have you opted out of AI training data collection where possible?',
          options: ['Yes, systematically reviewed and opted out (OpenAI, Google, Meta, etc.)', 'Sometimes', 'No/Not aware of this'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'ai_6',
          question: 'Do you use local/on-device AI models for sensitive tasks instead of cloud-based AI services?',
          options: ['Yes, use local models (Ollama, LM Studio, on-device) for sensitive work', 'Sometimes, based on sensitivity', 'Always use cloud AI without consideration'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'biometric',
      title: 'Biometric Security',
      icon: <Fingerprint className="h-6 w-6" />,
      questions: [
        {
          id: 'biometric_1',
          question: 'Do you use biometric authentication (fingerprint, face recognition) on your devices?',
          options: ['No, avoid biometrics or use only with strong fallback PIN', 'Yes, but with strong fallback protection', 'Yes, as primary authentication with weak fallback'],
          riskLevels: [0, 1, 2]
        },
        {
          id: 'biometric_2',
          question: 'Are you aware of how your biometric data is stored on the devices you use?',
          options: ['Yes, fully understand on-device vs. cloud storage mechanisms', 'Basic understanding', 'No knowledge'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'biometric_3',
          question: 'Do you use alternative authentication options when high security is needed?',
          options: ['Yes, use passkeys or hardware keys without biometrics for sensitive accounts', 'Sometimes', 'Rely primarily on biometrics'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'biometric_4',
          question: 'Are you aware of your legal rights regarding biometric data collection (BIPA, GDPR Article 9)?',
          options: ['Yes, comprehensive understanding', 'Basic awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'biometric_5',
          question: 'Have you ever been required to provide biometric data to access services?',
          options: ['No, avoid or find alternatives', 'Yes, but only for essential services', 'Yes, provide without consideration'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'biometric_6',
          question: 'Do you understand the permanence of biometric data breaches compared to password breaches?',
          options: ['Yes, fully understand — biometrics cannot be changed if compromised', 'Some understanding', 'No understanding'],
          riskLevels: [0, 1, 3]
        }
      ]
    },
    {
      id: 'financial',
      title: 'Financial Privacy',
      icon: <CreditCard className="h-6 w-6" />,
      questions: [
        {
          id: 'financial_1',
          question: 'Do you use privacy-preserving payment methods (cash, virtual cards, privacy-focused alternatives)?',
          options: ['Yes, regularly use privacy-preserving payment methods', 'Sometimes use virtual cards or cash', 'Always use standard traceable payment methods'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'financial_2',
          question: 'Are you aware of how different payment methods are tracked and profiled?',
          options: ['Yes, comprehensive understanding', 'Basic awareness', 'No awareness'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'financial_3',
          question: 'Do you take measures to protect financial statements and tax documents?',
          options: ['Yes, comprehensive protection (encrypted storage, secure shredding)', 'Basic measures', 'No special protection'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'financial_4',
          question: 'Are you aware of your digital footprint when making online purchases?',
          options: ['Yes, take active measures to minimize (virtual cards, aliases, etc.)', 'Some awareness', 'No consideration'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'financial_5',
          question: 'Do you use virtual cards or unique email addresses for online shopping?',
          options: ['Yes, consistently (Privacy.com, Revolut virtual cards, email aliases)', 'Sometimes', 'Never'],
          riskLevels: [0, 1, 3]
        },
        {
          id: 'financial_6',
          question: 'Have you frozen your credit and implemented fraud monitoring?',
          options: ['Yes, credit frozen at all bureaus + monitoring active', 'Basic monitoring only', 'No measures'],
          riskLevels: [0, 1, 3]
        }
      ]
    }
  ];

  const handleAnswer = async (questionId, answer, riskLevel) => {
    if (!(await ensureStarted())) return; // gate overlay shown — ignore until entitled
    setAnswers({
      ...answers,
      [questionId]: { answer, riskLevel }
    });
  };

  const calculateSectionScore = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    let totalRisk = 0;
    let answeredQuestions = 0;

    section.questions.forEach(q => {
      if (answers[q.id]) {
        totalRisk += answers[q.id].riskLevel;
        answeredQuestions++;
      }
    });

    if (answeredQuestions === 0) return null;
    return Math.max(0, 100 - (totalRisk / (answeredQuestions * 3)) * 100);
  };

  const calculateOverallScore = () => {
    let totalScore = 0;
    let sectionsWithScores = 0;

    sections.forEach(section => {
      const score = calculateSectionScore(section.id);
      if (score !== null) {
        totalScore += score;
        sectionsWithScores++;
      }
    });

    return sectionsWithScores > 0 ? Math.round(totalScore / sectionsWithScores) : 0;
  };

  const getScoreCategory = (score) => {
    if (score >= 80) return { label: 'Strong', color: 'text-green-500', icon: <Shield className="h-5 w-5 text-green-500" /> };
    if (score >= 60) return { label: 'Adequate', color: 'text-yellow-500', icon: <Shield className="h-5 w-5 text-yellow-500" /> };
    if (score >= 40) return { label: 'Vulnerable', color: 'text-orange-500', icon: <AlertTriangle className="h-5 w-5 text-orange-500" /> };
    return { label: 'High Risk', color: 'text-red-500', icon: <AlertTriangle className="h-5 w-5 text-red-500" /> };
  };

  const getRecommendation = (sectionId, score) => {
    if (sectionId === 'device') {
      if (score < 50) return 'Enable full-disk encryption (FileVault on Mac, BitLocker or VeraCrypt on Windows/Linux). Keep OS and apps updated automatically. Use strong PINs — avoid biometrics at border crossings where you may be compelled to unlock.';
      if (score < 80) return 'Review app permissions quarterly. Consider GrapheneOS or CalyxOS for Android, or hardening your current OS settings. Use hardware camera/mic covers for laptops.';
      return 'Maintain your strong device security practices and stay informed about new firmware vulnerabilities.';
    }
    if (sectionId === 'comms') {
      if (score < 50) return 'Switch to Signal for everyday messaging — it\'s the gold standard for E2EE. For a VPN, Mullvad (anonymous, no logs, accepts cash) or ProtonVPN are trustworthy. Enable disappearing messages.';
      if (score < 80) return 'Enable DNS-over-HTTPS using Quad9 (9.9.9.9) or NextDNS. Verify Signal safety numbers with key contacts. Use separate identities for different communication contexts.';
      return 'Continue using secure channels. Periodically re-verify safety numbers/security keys with important contacts as keys can change after reinstalls.';
    }
    if (sectionId === 'data') {
      if (score < 50) return 'Set up Bitwarden (free, open-source, audited) or 1Password as your password manager today. Use Proton Drive or Tresorit for encrypted cloud storage. Enable automatic encrypted backups with Backblaze.';
      if (score < 80) return 'Encrypt files before cloud upload using Cryptomator (works with any cloud provider). Run Facebook Privacy Checkup and review LinkedIn and other social media settings annually.';
      return 'Maintain your strong data protection habits. Periodically audit which third-party apps have OAuth access to your accounts and revoke what you no longer use.';
    }
    if (sectionId === 'location') {
      if (score < 50) return 'Disable location services by default in iOS Settings > Privacy & Security > Location Services or Android Settings > Location. Switch navigation to Organic Maps (free, offline, no tracking) or OsmAnd.';
      if (score < 80) return 'Audit location permissions quarterly. Disable "Significant Locations" and location history in your phone\'s privacy settings. Avoid posting photos with embedded GPS metadata.';
      return 'Continue minimizing your location footprint. Strip GPS metadata from photos before sharing using ExifTool or your OS\'s built-in option.';
    }
    if (sectionId === 'identity') {
      if (score < 50) return 'Enable proper 2FA using Ente Auth or Aegis (not SMS — SIM swap is a real threat). Use SimpleLogin or AnonAddy for email aliases. Remove yourself from data brokers via Incogni or DeleteMe. Freeze your credit now.';
      if (score < 80) return 'Upgrade SMS 2FA to passkeys or a YubiKey wherever supported. Run yourself through HaveIBeenPwned.com monthly. Check data brokers periodically — many re-list removed data within months.';
      return 'Excellent identity hygiene. Periodically run data broker searches as profiles can reappear. Consider a hardware security key (YubiKey 5) for your most sensitive accounts.';
    }
    if (sectionId === 'browser') {
      if (score < 50) return 'Switch to Firefox with uBlock Origin (essential, free) and Privacy Badger. Or use Brave browser. Disable WebRTC (in Firefox: about:config, set media.peerconnection.enabled to false). Disable Edge Copilot and Chrome AI features.';
      if (score < 80) return 'Use Firefox Multi-Account Containers or Brave profiles for compartmentalization. Try Mullvad Browser for high-anonymity sessions. Set cookies to clear on close. Review browser extensions — each is a potential privacy risk.';
      return 'Strong browser hygiene. Stay current on new fingerprinting techniques. Review browser AI features periodically — they evolve and may send new data types to the cloud.';
    }
    if (sectionId === 'network') {
      if (score < 50) return 'Change your router admin credentials right now — use a strong unique password. Set DNS to Quad9 (9.9.9.9) in your router settings. Create a separate guest network for IoT devices. Disable UPnP and WPS on your router.';
      if (score < 80) return 'Deploy Pi-hole or AdGuard Home for network-level blocking — it stops trackers across all devices including smart TVs. Monitor connected devices in your router dashboard monthly for unknown devices.';
      return 'Strong network security posture. Keep router firmware updated — enable auto-updates if available. Consider a VLAN-capable router for better IoT isolation.';
    }
    if (sectionId === 'opsec') {
      if (score < 50) return 'Start with threat modeling: who are your adversaries and what do they want? Use BleachBit (Windows/Linux) or secure empty trash (Mac) for file deletion. Strip metadata with MAT2 or ExifTool before sharing files.';
      if (score < 80) return 'Verify software downloads using GPG signatures — most major projects publish them. Consider an offline air-gapped device for managing crypto wallets or highly sensitive documents. Create a breach response checklist.';
      return 'Strong operational security. Stay informed about post-quantum cryptography — NIST finalized PQC standards in 2024 and major services are beginning migration.';
    }
    if (sectionId === 'physical') {
      if (score < 50) return 'Use a privacy screen filter on your laptop in public (3M makes good ones). Position your monitor away from windows and doors. Lock your screen automatically and when stepping away (Win+L / Cmd+Ctrl+Q on Mac).';
      if (score < 80) return 'Use a Faraday bag for your phone in sensitive meetings. A white noise machine defeats basic audio surveillance. Learn to identify hidden cameras in hotel rooms and Airbnbs — cheap RF detectors catch most.';
      return 'Strong physical security awareness. For high-sensitivity environments, consider a TSCM sweep before sensitive meetings. Stay informed about new surveillance hardware miniaturization.';
    }
    if (sectionId === 'govt') {
      if (score < 50) return 'Research FISA Section 702 (reauthorized 2024), the EU AI Act data provisions, and your local state privacy laws. Use Tor Browser for sensitive browsing. Signal\'s sealed sender and disappearing messages resist legal intercept.';
      if (score < 80) return 'Choose EU-based providers (Proton, Mullvad, Tutanota) for stronger legal protections under GDPR compared to US providers subject to NSLs. Implement Tor for anonymous browsing. Follow EFF (eff.org) for surveillance law developments.';
      return 'Strong government surveillance awareness. Follow EFF and Privacy International for ongoing legal framework changes. Consider data minimization — data that doesn\'t exist can\'t be compelled.';
    }
    if (sectionId === 'smarthome') {
      if (score < 50) return 'Change default passwords on every IoT device to unique strong credentials — use your password manager. Enable auto-updates or check firmware quarterly. Create a dedicated IoT VLAN or at minimum a guest network to isolate these devices from your computers.';
      if (score < 80) return 'Disable "help improve" / telemetry options on each smart device. Before buying new devices, check PrivacySpy.org for manufacturer privacy ratings. Consider Matter-compatible devices for local control without cloud dependency.';
      return 'Good smart home security. Revisit each device\'s privacy settings annually as manufacturers update their data policies. Consider whether devices you rarely use justify their privacy footprint.';
    }
    if (sectionId === 'ai') {
      if (score < 50) return 'Audit what AI tools have access to: revoke ChatGPT/Copilot/Gemini plugin permissions to your email, files, and calendar unless specifically needed. Disable voice assistant always-on wake words. Opt out of AI training at OpenAI (Settings > Data Controls), Google (My Activity), and Meta.';
      if (score < 80) return 'For sensitive work, try local AI models via Ollama or LM Studio — your data never leaves your device. Review the EU AI Act rights that apply to AI systems you interact with. Be skeptical of AI voice/video calls — deepfake audio is now trivial to generate.';
      return 'Strong AI privacy posture. Stay informed about agentic AI developments — AI agents with broad account access are an emerging privacy risk. Periodically re-audit what data access you\'ve granted to AI services.';
    }
    if (sectionId === 'biometric') {
      if (score < 50) return 'Use strong PINs as primary authentication for high-security accounts instead of biometrics. Remember: unlike passwords, biometrics cannot be changed if compromised. Learn your rights under BIPA (US) or GDPR Article 9 (EU) for biometric data.';
      if (score < 80) return 'For your most sensitive accounts (banking, email), use a hardware security key or passkeys instead of biometrics. Maintain strong PIN fallbacks on all biometric-enabled devices — fingerprint readers can be bypassed.';
      return 'Cautious and well-informed biometric approach. Follow BIPA litigation and GDPR enforcement actions for evolving biometric rights. Passkeys are increasingly replacing both passwords and biometrics as the preferred standard.';
    }
    if (sectionId === 'financial') {
      if (score < 50) return 'Freeze your credit at Equifax, Experian, and TransUnion right now — it\'s free and takes 10 minutes per bureau. Use Privacy.com virtual cards for online shopping. Set up unique email aliases for all financial accounts.';
      if (score < 80) return 'Enable real-time transaction alerts on all accounts. Use Privacy.com or Revolut virtual cards for purchases you prefer unlinkable. Audit which apps have Plaid/open banking access to your accounts and revoke unnecessary ones.';
      return 'Strong financial privacy practices. Periodically review open banking app authorizations via your bank\'s connected apps settings. Consider a dedicated email address for all financial correspondence.';
    }
    return 'Review security best practices and address vulnerabilities in this category.';
  };

  const exportResults = () => {
    const overallScore = calculateOverallScore();
    const category = getScoreCategory(overallScore);
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const lines = [
      `Privacy Assessment Results — ${date}`,
      `Overall Score: ${overallScore}/100 (${category.label})`,
      '',
      'Section Scores:',
      ...sections.map(s => {
        const score = calculateSectionScore(s.id);
        return score !== null
          ? `  ${s.title}: ${Math.round(score)}/100`
          : `  ${s.title}: Not answered`;
      }),
      '',
      'Top Priority Areas:',
      ...sections
        .map(s => ({ section: s, score: calculateSectionScore(s.id) }))
        .filter(i => i.score !== null)
        .sort((a, b) => a.score - b.score)
        .slice(0, 3)
        .map(i => `  • ${i.section.title} (${Math.round(i.score)}/100):\n    ${getRecommendation(i.section.id, i.score)}`),
      '',
      'Generated at me.myprivacy.blog'
    ];

    const text = lines.join('\n');

    if (navigator.share) {
      navigator.share({ title: 'My Privacy Assessment Results', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setCopyConfirm(true);
        setTimeout(() => setCopyConfirm(false), 2500);
      });
    }
  };

  const toggleExpand = (sectionId) => {
    setExpanded({
      ...expanded,
      [sectionId]: !expanded[sectionId]
    });
  };

  const getProgressPercentage = () => {
    const totalQuestions = sections.reduce((sum, section) => sum + section.questions.length, 0);
    const answeredQuestions = Object.keys(answers).length;
    return (answeredQuestions / totalQuestions) * 100;
  };

  const handleStartOver = () => {
    localStorage.removeItem('privacyAssessment_answers');
    setAnswers({});
    setShowResults(false);
    setCurrentSection(0);
    setExpanded({});
  };

  return (
    <div className="max-w-2xl mx-auto p-4 bg-stone-100 rounded-lg shadow-lg">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center justify-center gap-2">
          <Lock className="h-6 w-6 text-blue-600" />
          Digital Privacy Vulnerability Assessment
        </h1>
        <p className="text-gray-600 mt-2">Evaluate your exposure to surveillance and tracking</p>

        {hasSavedProgress && !showResults && (
          <div className="mt-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-1.5 inline-flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Progress auto-saved — pick up where you left off
          </div>
        )}

        <div className="mt-6 bg-blue-50 p-4 rounded-lg text-left border border-blue-200">
          <h2 className="text-lg font-semibold text-blue-800 mb-2">Protect Your Digital Life in 2026</h2>
          <p className="text-blue-700 mb-3">
            Pervasive surveillance, AI profiling, and agentic systems now touch every corner of your digital life. This assessment helps you identify and close privacy gaps across all threat surfaces.
          </p>
          <p className="text-blue-700 mb-1">
            This tool evaluates your security posture across multiple domains:
          </p>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-3 text-sm text-blue-700">
            <div className="flex items-center"><Shield className="h-4 w-4 mr-1 text-blue-600" /> Device Security</div>
            <div className="flex items-center"><Globe className="h-4 w-4 mr-1 text-blue-600" /> Communications</div>
            <div className="flex items-center"><Server className="h-4 w-4 mr-1 text-blue-600" /> Data Protection</div>
            <div className="flex items-center"><Eye className="h-4 w-4 mr-1 text-blue-600" /> Identity Protection</div>
            <div className="flex items-center"><Wifi className="h-4 w-4 mr-1 text-blue-600" /> Network Security</div>
            <div className="flex items-center"><AlertCircle className="h-4 w-4 mr-1 text-blue-600" /> Government Surveillance</div>
            <div className="flex items-center"><Home className="h-4 w-4 mr-1 text-blue-600" /> Smart Home</div>
            <div className="flex items-center"><Brain className="h-4 w-4 mr-1 text-blue-600" /> AI & ML Exposure</div>
            <div className="flex items-center"><Fingerprint className="h-4 w-4 mr-1 text-blue-600" /> Biometric Security</div>
            <div className="flex items-center"><CreditCard className="h-4 w-4 mr-1 text-blue-600" /> Financial Privacy</div>
          </div>
          <button
            className="text-blue-600 font-medium hover:text-blue-800 flex items-center"
            onClick={() => {
              setModalContent('main');
              setShowModal(true);
            }}
          >
            <HelpCircle className="h-4 w-4 mr-1" /> How It Works
          </button>
        </div>
      </header>

      {!showResults ? (
        <>
          <div className="mb-4 bg-stone-50 p-4 rounded-md shadow">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {sections[currentSection].icon}
                {sections[currentSection].title}
              </h2>
              <span className="text-sm text-gray-500">Section {currentSection + 1} of {sections.length}</span>
            </div>
            <div className="h-2 w-full bg-stone-300 rounded-full mb-4">
              <div
                className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>

            {sections[currentSection].questions.map((q, idx) => (
              <div key={q.id} className="mb-6 last:mb-0">
                <p className="mb-2 font-medium">{idx + 1}. {q.question}</p>
                <div className="space-y-2">
                  {q.options.map((option, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleAnswer(q.id, option, q.riskLevels[optIdx])}
                      className={`w-full text-left p-3 rounded-md transition-colors ${
                        answers[q.id] && answers[q.id].answer === option
                          ? 'bg-blue-100 border border-blue-300'
                          : 'bg-stone-50 border border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setCurrentSection(currentSection - 1)}
                disabled={currentSection === 0}
                className={`px-4 py-2 rounded ${
                  currentSection === 0
                    ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                }`}
              >
                Previous
              </button>

              <button
                onClick={() => {
                  if (currentSection < sections.length - 1) {
                    setCurrentSection(currentSection + 1);
                  } else {
                    setShowResults(true);
                    // Save to the member portal's Reports tab (logged-in only; no-op for guests).
                    const gate = window.csmGate;
                    if (gate?.isLoggedIn?.() && gate.saveReport) {
                      const overall = calculateOverallScore();
                      gate.saveReport({ title: 'My Privacy Profile', summary: `Personal privacy — ${overall}%`, data: { answers, overall } });
                    }
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {currentSection < sections.length - 1 ? 'Next' : 'See Results'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="results">
          <div className="mb-6 text-center p-6 bg-stone-50 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-2">Your Overall Privacy Score</h2>
            <div className="relative inline-block">
              <svg className="w-32 h-32" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={calculateOverallScore() >= 80 ? "#10b981" : calculateOverallScore() >= 60 ? "#f59e0b" : calculateOverallScore() >= 40 ? "#f97316" : "#ef4444"}
                  strokeWidth="3"
                  strokeDasharray={`${calculateOverallScore()}, 100`}
                />
              </svg>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold">
                {calculateOverallScore()}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 text-lg font-medium">
              {getScoreCategory(calculateOverallScore()).icon}
              <span className={getScoreCategory(calculateOverallScore()).color}>
                {getScoreCategory(calculateOverallScore()).label}
              </span>
            </div>
            <button
              onClick={exportResults}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 mx-auto text-sm"
            >
              <Download className="h-4 w-4" />
              {copyConfirm ? 'Copied to clipboard!' : 'Export / Share Results'}
            </button>
          </div>

          <div className="space-y-4">
            {sections.map(section => {
              const sectionScore = calculateSectionScore(section.id);
              return (
                <div key={section.id} className="bg-stone-50 p-4 rounded-lg shadow">
                  <div
                    onClick={() => toggleExpand(section.id)}
                    className="flex justify-between items-center cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {section.icon}
                      <h3 className="font-semibold">{section.title}</h3>
                      {sectionScore !== null && (
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          sectionScore >= 80 ? 'bg-green-100 text-green-800' :
                          sectionScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          sectionScore >= 40 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          Score: {Math.round(sectionScore)}
                        </span>
                      )}
                    </div>
                    <button>
                      {expanded[section.id] ? (
                        <X className="h-5 w-5 text-gray-500" />
                      ) : (
                        <span className="text-sm text-blue-600">Details</span>
                      )}
                    </button>
                  </div>

                  {expanded[section.id] && (
                    <div className="mt-4">
                      {sectionScore !== null ? (
                        <>
                          <div className="mb-4 p-3 bg-stone-100 rounded-md">
                            <h4 className="font-medium text-gray-700 mb-1">Recommendation:</h4>
                            <p className="text-gray-600">{getRecommendation(section.id, sectionScore)}</p>
                          </div>
                          <h4 className="font-medium text-gray-700 mb-2">Your Responses:</h4>
                          <ul className="space-y-2">
                            {section.questions.map(q => (
                              <li key={q.id} className="flex items-start gap-2 text-sm">
                                {answers[q.id] ? (
                                  <>
                                    <span className={`mt-0.5 ${
                                      answers[q.id].riskLevel === 0 ? 'text-green-500' :
                                      answers[q.id].riskLevel === 1 ? 'text-yellow-500' :
                                      answers[q.id].riskLevel === 2 ? 'text-orange-500' :
                                      'text-red-500'
                                    }`}>
                                      {answers[q.id].riskLevel === 0 ? (
                                        <CheckCircle className="h-4 w-4" />
                                      ) : (
                                        <AlertTriangle className="h-4 w-4" />
                                      )}
                                    </span>
                                    <div>
                                      <p className="text-gray-800">{q.question}</p>
                                      <p className="text-gray-600">Your answer: {answers[q.id].answer}</p>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-gray-300 mt-0.5">
                                      <X className="h-4 w-4" />
                                    </span>
                                    <div>
                                      <p className="text-gray-400">{q.question}</p>
                                      <p className="text-gray-400">Not answered</p>
                                    </div>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p className="text-gray-500 italic">No questions answered in this section</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Next Steps
            </h3>
            <p className="text-blue-700 mb-2">Focus on addressing your highest risk areas first:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              {sections
                .map(section => ({
                  section,
                  score: calculateSectionScore(section.id)
                }))
                .filter(item => item.score !== null)
                .sort((a, b) => a.score - b.score)
                .slice(0, 3)
                .map(item => (
                  <li key={item.section.id}>
                    <span className="font-medium">{item.section.title}</span>: {getRecommendation(item.section.id, item.score)}
                  </li>
                ))}
            </ol>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handleStartOver}
              className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300 flex items-center gap-2 mx-auto"
            >
              <RotateCcw className="h-4 w-4" /> Start Over
            </button>
          </div>
        </div>
      )}

      <footer className="mt-8 text-center text-sm">
        <div className="bg-stone-200 p-4 rounded-lg border border-stone-300">
          <p className="text-gray-700 mb-2">This tool is for educational purposes only and does not guarantee complete privacy protection.</p>
          <p className="text-gray-700 mb-3">We don't store your assessment data — all evaluation happens locally in your browser. Progress is saved to your device only.</p>
          <div className="flex flex-wrap justify-center gap-3 mb-3">
            <button
              className="text-blue-600 text-xs hover:text-blue-800 flex items-center"
              onClick={() => {
                setModalContent('resources');
                setShowModal(true);
              }}
            >
              <Info className="h-3 w-3 mr-1" /> Privacy Resources
            </button>
            <button
              className="text-blue-600 text-xs hover:text-blue-800 flex items-center"
              onClick={() => {
                setModalContent('methodology');
                setShowModal(true);
              }}
            >
              <Info className="h-3 w-3 mr-1" /> Assessment Methodology
            </button>
            <button
              className="text-blue-600 text-xs hover:text-blue-800 flex items-center"
              onClick={() => {
                setModalContent('categories');
                setShowModal(true);
              }}
            >
              <Info className="h-3 w-3 mr-1" /> Category Explanations
            </button>
          </div>
          <p className="text-gray-500 text-xs">© 2026 Privacy Posture. Always consult security professionals for critical privacy concerns.</p>
        </div>
      </footer>

      {showWelcome && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-50 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 rounded-full p-3">
                  <Shield className="h-7 w-7 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Welcome to the Privacy Assessment</h2>
                  <p className="text-sm text-gray-500">Know your exposure. Close the gaps.</p>
                </div>
              </div>

              <p className="text-gray-700 mb-4">
                This tool scores your digital privacy posture across <strong>14 threat domains</strong> — from device security and encrypted messaging to AI agent exposure, biometric risks, and financial privacy. It takes about <strong>10–15 minutes</strong> to complete.
              </p>

              <div className="bg-stone-100 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">What you'll cover</h3>
                <div className="grid grid-cols-2 gap-1 text-sm text-gray-700">
                  <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Device Security</div>
                  <div className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Communications</div>
                  <div className="flex items-center gap-1.5"><Server className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Data Protection</div>
                  <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Location Privacy</div>
                  <div className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Identity Protection</div>
                  <div className="flex items-center gap-1.5"><Wifi className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Network Security</div>
                  <div className="flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Smart Home</div>
                  <div className="flex items-center gap-1.5"><Brain className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />AI & ML Exposure</div>
                  <div className="flex items-center gap-1.5"><Fingerprint className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Biometric Security</div>
                  <div className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Financial Privacy</div>
                  <div className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Physical Security</div>
                  <div className="flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />Gov. Surveillance</div>
                </div>
              </div>

              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3 mb-5 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-800">
                  <strong>Fully private:</strong> All scoring happens in your browser. Nothing is sent to any server. Your answers are saved locally on your device so you can pause and resume any time.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={dismissWelcome}
                  className="flex-1 px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-center"
                >
                  Start Assessment
                </button>
                <button
                  onClick={() => {
                    dismissWelcome();
                    setModalContent('main');
                    setShowModal(true);
                  }}
                  className="flex-1 px-5 py-3 bg-stone-200 text-stone-700 font-medium rounded-lg hover:bg-stone-300 transition-colors text-center"
                >
                  How It Works First
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-50 rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b sticky top-0 bg-stone-50 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {modalContent === 'main' && 'How This Assessment Works'}
                {modalContent === 'resources' && 'Privacy Resources'}
                {modalContent === 'methodology' && 'Assessment Methodology'}
                {modalContent === 'categories' && 'Category Explanations'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {modalContent === 'main' && (
                <div>
                  <p className="mb-4">This privacy assessment evaluates your digital security practices across 14 domains to help identify vulnerabilities in your privacy posture. Scoring runs in your browser; signed-in CISO Marketplace members can save results to their portal. Run with membership, credits, a Tools plan, or a one-time pass — see <a href="https://microsec.tools/pricing" className="text-blue-600 underline">pricing</a>.</p>

                  <h3 className="font-semibold text-lg mb-2">How to Use This Tool</h3>
                  <ol className="list-decimal list-inside mb-4 space-y-1">
                    <li>Complete the assessment by answering questions across all categories</li>
                    <li>Review your detailed score breakdown by category</li>
                    <li>Follow the personalized recommendations to improve your privacy</li>
                    <li>Export your results and share or save them for reference</li>
                    <li>Reassess every 6–12 months as threats and your practices evolve</li>
                  </ol>

                  <h3 className="font-semibold text-lg mb-2">Scoring System</h3>
                  <p className="mb-3">Your responses are evaluated on a risk scale from 0 (secure practice) to 3 (high risk), converted to a 0-100 score where:</p>
                  <ul className="mb-4 space-y-1">
                    <li className="flex items-center"><span className="inline-block w-4 h-4 rounded-full bg-green-500 mr-2"></span> <strong>80–100:</strong> Strong protection</li>
                    <li className="flex items-center"><span className="inline-block w-4 h-4 rounded-full bg-yellow-500 mr-2"></span> <strong>60–79:</strong> Adequate protection</li>
                    <li className="flex items-center"><span className="inline-block w-4 h-4 rounded-full bg-orange-500 mr-2"></span> <strong>40–59:</strong> Vulnerable</li>
                    <li className="flex items-center"><span className="inline-block w-4 h-4 rounded-full bg-red-500 mr-2"></span> <strong>0–39:</strong> High risk</li>
                  </ul>

                  <div className="flex justify-between mt-6">
                    <button
                      onClick={() => setModalContent('categories')}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Category Explanations
                    </button>
                    <button
                      onClick={() => setModalContent('methodology')}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Assessment Methodology
                    </button>
                  </div>
                </div>
              )}

              {modalContent === 'resources' && (
                <div>
                  <p className="mb-4">Vetted tools and resources to improve your privacy posture. These are community-trusted, not sponsored recommendations.</p>

                  <h3 className="font-semibold text-lg mb-2">Secure Messaging</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Signal</strong> — Best overall encrypted messenger, open source</li>
                    <li><strong>Element / Matrix</strong> — Decentralized, self-hostable E2EE chat</li>
                    <li><strong>Briar</strong> — P2P messaging, works without internet via Bluetooth/WiFi</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">VPN & Anonymity</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Mullvad VPN</strong> — No accounts, accepts cash, audited no-logs policy</li>
                    <li><strong>ProtonVPN</strong> — Swiss-based, open source, free tier available</li>
                    <li><strong>Tor Browser</strong> — Best anonymity, use for sensitive browsing</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Email & Aliases</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Proton Mail</strong> — E2EE email, EU-based, audited</li>
                    <li><strong>Tuta (Tutanota)</strong> — E2EE email, EU-based, open source</li>
                    <li><strong>SimpleLogin</strong> — Email alias service (now owned by Proton)</li>
                    <li><strong>AnonAddy</strong> — Open source email alias service</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Password Managers</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Bitwarden</strong> — Free, open source, self-hostable, audited</li>
                    <li><strong>1Password</strong> — Polished, travel mode, family plans</li>
                    <li><strong>KeePassXC</strong> — Fully local, no sync, maximum control</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Browsers & Extensions</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Firefox</strong> — Best mainstream privacy browser with proper extension support</li>
                    <li><strong>Brave</strong> — Built-in ad/tracker blocking, fingerprint randomization</li>
                    <li><strong>Mullvad Browser</strong> — Hardened Firefox fork for anonymity</li>
                    <li><strong>uBlock Origin</strong> — Essential ad/tracker blocker (Firefox/Chrome)</li>
                    <li><strong>Privacy Badger</strong> — Behavioral tracker detection (EFF)</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Data Broker Removal</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Incogni</strong> — Automated removal from 180+ data brokers</li>
                    <li><strong>DeleteMe</strong> — Manual + service-assisted removal</li>
                    <li><strong>JustDeleteMe</strong> — Links to account deletion pages for 500+ services</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Mobile OS</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>GrapheneOS</strong> — Hardened Android (Pixel), sandboxed Google Play</li>
                    <li><strong>CalyxOS</strong> — Privacy-focused Android, includes microG</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Educational Resources</h3>
                  <ul className="list-disc list-inside mb-4 space-y-1 text-sm">
                    <li><strong>Privacy Guides</strong> (privacyguides.org) — Comprehensive vetted tool recommendations</li>
                    <li><strong>EFF Surveillance Self-Defense</strong> (ssd.eff.org) — Threat model guides</li>
                    <li><strong>The Hitchhiker's Guide to Online Anonymity</strong> — Advanced OpSec guide</li>
                    <li><strong>Privacy International</strong> (privacyinternational.org) — Policy and legal resources</li>
                  </ul>

                  <button
                    onClick={() => setModalContent('main')}
                    className="mt-4 px-3 py-1 bg-stone-200 text-stone-700 rounded hover:bg-stone-300 text-sm"
                  >
                    Back to Main
                  </button>
                </div>
              )}

              {modalContent === 'methodology' && (
                <div>
                  <p className="mb-4">This assessment uses a comprehensive methodology to evaluate your privacy posture against modern threat models.</p>

                  <h3 className="font-semibold text-lg mb-2">Assessment Framework</h3>
                  <p className="mb-3">The assessment evaluates your practices against a 2026 threat model that includes:</p>
                  <ul className="list-disc list-inside mb-4 space-y-1">
                    <li>Commercial data collection and surveillance capitalism</li>
                    <li>AI-driven profiling and algorithmic decision-making</li>
                    <li>Agentic AI systems with broad account access</li>
                    <li>Network adversaries and passive/active monitoring</li>
                    <li>Targeted attacks from sophisticated actors</li>
                    <li>Mass surveillance programs and legal intercept obligations</li>
                    <li>Physical security vulnerabilities and biometric collection</li>
                  </ul>

                  <h3 className="font-semibold text-lg mb-2">Scoring Methodology</h3>
                  <p className="mb-3">Each response is assigned a risk level:</p>
                  <ul className="mb-4 space-y-1">
                    <li><strong>0:</strong> Best practice (minimal risk)</li>
                    <li><strong>1:</strong> Acceptable practice (low risk)</li>
                    <li><strong>2:</strong> Suboptimal practice (medium risk)</li>
                    <li><strong>3:</strong> Vulnerable practice (high risk)</li>
                  </ul>
                  <p className="mb-4">Category scores are the average question risk converted to 0–100. The overall score is the average of all answered category scores. Unanswered categories do not penalize your score.</p>

                  <h3 className="font-semibold text-lg mb-2">Limitations</h3>
                  <p className="mb-3">Self-reported assessments have inherent limitations. This tool provides directional guidance, not a comprehensive security audit. For high-risk individuals (journalists, activists, executives), consult a professional security consultant and review EFF's threat modeling resources.</p>

                  <button
                    onClick={() => setModalContent('main')}
                    className="mt-4 px-3 py-1 bg-stone-200 text-stone-700 rounded hover:bg-stone-300 text-sm"
                  >
                    Back to Main
                  </button>
                </div>
              )}

              {modalContent === 'categories' && (
                <div>
                  <p className="mb-4">The assessment evaluates these 14 privacy and security domains:</p>

                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Smartphone className="h-5 w-5 mr-1 text-blue-600" /> Device Security</h3>
                      <p className="text-sm">Encryption, updates, lock screen, app permissions, hardened OS options, and hardware security measures.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Globe className="h-5 w-5 mr-1 text-blue-600" /> Communications Security</h3>
                      <p className="text-sm">E2EE messaging, VPN usage, DNS privacy, ephemeral messaging, security key verification, and identity compartmentalization.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Server className="h-5 w-5 mr-1 text-blue-600" /> Data Protection</h3>
                      <p className="text-sm">Cloud encryption, password management, backup practices, and social media privacy settings.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><MapPin className="h-5 w-5 mr-1 text-blue-600" /> Location Privacy</h3>
                      <p className="text-sm">Location service controls, social sharing habits, privacy-respecting navigation apps, and app permission audits.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Eye className="h-5 w-5 mr-1 text-blue-600" /> Identity Protection</h3>
                      <p className="text-sm">MFA type (passkeys vs TOTP vs SMS), online information exposure, email aliases, data broker removal, and username uniqueness.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Globe className="h-5 w-5 mr-1 text-blue-600" /> Browser Security</h3>
                      <p className="text-sm">Privacy browser and extension usage, WebRTC leaks, cookie clearing, compartmentalization, browser AI features, and fingerprinting resistance.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Wifi className="h-5 w-5 mr-1 text-blue-600" /> Network Security</h3>
                      <p className="text-sm">Router credentials, IoT isolation, privacy DNS, network-level ad blocking, traffic monitoring, and unused service hardening.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Layers className="h-5 w-5 mr-1 text-blue-600" /> Advanced Operational Security</h3>
                      <p className="text-sm">Threat modeling, secure deletion, air-gapped computing, metadata stripping, software signature verification, and incident response planning.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Camera className="h-5 w-5 mr-1 text-blue-600" /> Physical Security & Surveillance</h3>
                      <p className="text-sm">Camera avoidance awareness, audio surveillance countermeasures, visual privacy practices, TSCM knowledge, environmental awareness, and physical document security.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><AlertCircle className="h-5 w-5 mr-1 text-blue-600" /> Government-Level Surveillance</h3>
                      <p className="text-sm">Current surveillance law awareness (FISA, EU AI Act), legal intercept knowledge, data sovereignty, anonymity tools, and traffic analysis countermeasures.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Home className="h-5 w-5 mr-1 text-blue-600" /> Smart Home Security</h3>
                      <p className="text-sm">IoT firmware updates, credential hygiene, network isolation, data minimization settings, microphone/camera controls, and manufacturer privacy research.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Brain className="h-5 w-5 mr-1 text-blue-600" /> AI & ML Exposure</h3>
                      <p className="text-sm">Facial recognition countermeasures, AI agent access controls, algorithmic profiling awareness, deepfake/voice clone risks, AI training opt-outs, and local vs. cloud AI usage.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><Fingerprint className="h-5 w-5 mr-1 text-blue-600" /> Biometric Security</h3>
                      <p className="text-sm">Biometric authentication use, storage mechanism awareness, alternative auth for high-security accounts, legal rights, and permanence risk understanding.</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 flex items-center"><CreditCard className="h-5 w-5 mr-1 text-blue-600" /> Financial Privacy</h3>
                      <p className="text-sm">Privacy-preserving payment methods, payment tracking awareness, financial document protection, online purchase footprint, virtual card usage, and credit monitoring.</p>
                    </div>
                  </div>

                  <button
                    onClick={() => setModalContent('main')}
                    className="mt-6 px-3 py-1 bg-stone-200 text-stone-700 rounded hover:bg-stone-300 text-sm"
                  >
                    Back to Main
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyAssessment;
